import React, { useState, useRef } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Skeletons, EmptyState } from "@/components/common/EmptyState";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { fileUrl, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AddressBook, Plus, PencilSimple, Trash, Phone, MapPin, UploadSimple, Eye, DownloadSimple, FileText, FilePdf, FileXls, FileDoc, FileImage } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_CATEGORIES = ["Proposal", "Quotation", "Rate Card", "Contract/Agreement", "Invoice", "Media Plan", "Campaign Report", "Agency Profile", "Other"];
const FIELDS = [
  { name: "name", label: "Contact / Company Name", required: true },
  { name: "category", label: "Category / Role" },
  { name: "platform", label: "Type", type: "select", options: ["Instagram", "YouTube", "Pinterest", "X", "Agency", "Studio", "Photographer", "Stylist", "PR Agency", "Other"] },
  { name: "contact", label: "Contact Number", placeholder: "+91 98765 43210" },
  { name: "location", label: "Location" },
  { name: "rate", label: "Rate (₹)", type: "number" },
  { name: "stage", label: "Stage", type: "select", options: ["Identified", "Shortlisted", "Contacted", "Negotiation", "Confirmed", "Published"] },
  { name: "notes", label: "Notes", type: "textarea" },
];

const rateLabel = (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "Not Added");
const fmtSize = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : b >= 1024 ? Math.round(b / 1024) + " KB" : (b || 0) + " B");
const docIcon = (name = "") => {
  const e = name.split(".").pop().toLowerCase();
  if (e === "pdf") return FilePdf;
  if (["xls", "xlsx"].includes(e)) return FileXls;
  if (["doc", "docx"].includes(e)) return FileDoc;
  if (["jpg", "jpeg", "png"].includes(e)) return FileImage;
  return FileText;
};

export default function CollaboratorCRM() {
  const { items, isLoading, create, update, remove } = useResource("influencers");
  const { isApprover, user } = useAuth();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [docConfirm, setDocConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docCategory, setDocCategory] = useState("Proposal");

  const startEdit = (c) => { setEditItem(c); setOpen(true); setSelected(null); };
  const submit = (body) => (editItem ? update.mutateAsync({ id: editItem.id, body }) : create.mutateAsync({ ...body, avatar: `https://api.dicebear.com/7.x/notionists/svg?seed=${body.name}`, documents: [] }));
  const del = async (c) => { try { await remove.mutateAsync(c.id); toast.success("Deleted"); setSelected(null); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } setConfirm(null); };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selected) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const r = await uploadFile(f, { tags: "collab-doc" });
        uploaded.push({ id: r.id, name: r.name, url: r.url, storage_path: r.storage_path, content_type: r.content_type, size: r.size, category: docCategory, uploaded_by: user?.name, created_at: new Date().toISOString() });
      }
      const docs = [...(selected.documents || []), ...uploaded];
      await update.mutateAsync({ id: selected.id, body: { documents: docs } });
      setSelected({ ...selected, documents: docs });
      toast.success(`${files.length} document(s) uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false); e.target.value = "";
  };
  const removeDoc = async (doc) => {
    try {
      const docs = (selected.documents || []).filter((d) => d.id !== doc.id);
      await update.mutateAsync({ id: selected.id, body: { documents: docs } });
      setSelected({ ...selected, documents: docs });
      toast.success("Document deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setDocConfirm(null);
  };
  const download = async (doc) => {
    try {
      const res = await fetch(fileUrl(doc.url));
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = u; a.download = doc.name; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div>
      <input ref={fileRef} type="file" multiple hidden onChange={onUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" data-testid="collab-file-input" />
      <PageHeader title="Collaboration CRM" subtitle="Stylists, photographers, agencies & creators" icon={AddressBook}
        actions={<Button onClick={() => { setEditItem(null); setOpen(true); }} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> Add Collaborator</Button>} />

      {isLoading ? <Skeletons /> : items.length === 0 ? <EmptyState title="No collaborators yet" action={<Button onClick={() => setOpen(true)} className="rounded-full">Add Collaborator</Button>} /> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c, i) => (
            <div key={c.id} data-testid="influencers-card" className="group rounded-2xl border border-border bg-surface-raised p-5 transition-transform hover:-translate-y-1" style={{ animation: `fade-up 0.4s ease-out ${i * 0.03}s both` }}>
              <div onClick={() => setSelected(c)} className="cursor-pointer">
                <div className="flex items-center gap-3">
                  <Avatar className="h-11 w-11"><AvatarImage src={c.avatar} /><AvatarFallback>{c.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0"><p className="truncate font-display font-bold">{c.name}</p><p className="truncate text-xs text-muted-foreground">{[c.platform, c.category].filter(Boolean).join(" · ") || "—"}</p></div>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {c.contact && <p className="flex items-center gap-2 text-muted-foreground"><Phone size={14} /> {c.contact}</p>}
                  {c.location && <p className="flex items-center gap-2 text-muted-foreground"><MapPin size={14} /> {c.location}</p>}
                  <p className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Rate</span><span className="font-mono font-semibold">{rateLabel(c.rate)}</span></p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.stage} />
                  <button onClick={() => setSelected(c)} data-testid="open-documents" className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary hover:text-primary"><FileText size={13} /> Documents ({c.documents?.length || 0})</button>
                </div>
                <div className="flex opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => startEdit(c)} data-testid="edit-btn" className="p-1 text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                  {isApprover && <button onClick={() => setConfirm(c)} data-testid="delete-btn" className="p-1 text-muted-foreground hover:text-brand-pink"><Trash size={15} /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="collaborator-drawer">
          {selected && (<>
            <SheetHeader><SheetTitle className="font-display text-xl">{selected.name}</SheetTitle><SheetDescription className="sr-only">Collaborator details and documents.</SheetDescription></SheetHeader>
            <div className="mt-2 flex items-center gap-2"><StatusBadge status={selected.stage} /><span className="text-sm text-muted-foreground">{selected.platform} · {selected.category || "—"}</span></div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border border-border bg-surface-raised p-3"><p className="text-xs uppercase text-muted-foreground">Contact</p>{selected.contact ? <a href={`tel:${selected.contact}`} className="font-semibold text-primary" data-testid="collab-contact">{selected.contact}</a> : <p className="text-muted-foreground">Not added</p>}</div>
              <div className="rounded-xl border border-border bg-surface-raised p-3"><p className="text-xs uppercase text-muted-foreground">Rate</p><p className="font-semibold">{rateLabel(selected.rate)}</p></div>
              <div className="rounded-xl border border-border bg-surface-raised p-3"><p className="text-xs uppercase text-muted-foreground">Location</p><p className="font-semibold">{selected.location || "—"}</p></div>
              <div className="rounded-xl border border-border bg-surface-raised p-3"><p className="text-xs uppercase text-muted-foreground">Stage</p><p className="font-semibold">{selected.stage || "—"}</p></div>
            </div>
            {selected.notes && <div className="mt-3 rounded-xl border border-border bg-surface-raised p-3"><p className="mb-1 text-xs uppercase text-muted-foreground">Notes</p><p className="text-sm">{selected.notes}</p></div>}

            <div className="mt-5">
              <div className="mb-3 flex items-center justify-between">
                <p className="font-display font-bold">Documents <span className="text-sm font-normal text-muted-foreground">({(selected.documents || []).length})</span></p>
                <div className="flex items-center gap-2">
                  <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} className="h-9 rounded-lg border border-input bg-surface-raised px-2 text-xs" data-testid="doc-category">{DOC_CATEGORIES.map((d) => <option key={d}>{d}</option>)}</select>
                  <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload Document"}</Button>
                </div>
              </div>
              <div className="space-y-2">
                {(selected.documents || []).length === 0 ? <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No documents yet. Upload proposals, contracts, rate cards & more.</p> : (selected.documents || []).map((doc) => {
                  const Icon = docIcon(doc.name);
                  return (
                    <div key={doc.id} data-testid="doc-item" className="flex items-center gap-3 rounded-xl border border-border bg-surface-raised p-3">
                      <Icon size={26} weight="duotone" className="shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{doc.name}</p>
                        <p className="text-[11px] text-muted-foreground"><span className="rounded bg-secondary px-1.5 py-0.5">{doc.category}</span> · {fmtSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString("en-IN")} · {doc.uploaded_by || "—"}</p>
                      </div>
                      <button onClick={() => window.open(fileUrl(doc.url), "_blank")} data-testid="doc-view" title="View" className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><Eye size={16} /></button>
                      <button onClick={() => download(doc)} data-testid="doc-download" title="Download" className="rounded-lg p-1.5 text-muted-foreground hover:text-brand-mint"><DownloadSimple size={16} /></button>
                      <button onClick={() => setDocConfirm(doc)} data-testid="doc-delete" title="Delete" className="rounded-lg p-1.5 text-muted-foreground hover:text-brand-pink"><Trash size={16} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <Button onClick={() => startEdit(selected)} data-testid="collaborator-edit" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><PencilSimple size={14} className="mr-1" /> Edit</Button>
              {isApprover && <Button variant="outline" onClick={() => setConfirm(selected)} className="rounded-full text-brand-pink"><Trash size={14} className="mr-1" /> Delete</Button>}
            </div>
          </>)}
        </SheetContent>
      </Sheet>

      <CreateDrawer open={open} onOpenChange={setOpen} title="Collaborator" fields={FIELDS} mode={editItem ? "edit" : "create"} initial={editItem} submitting={create.isPending || update.isPending} onSubmit={submit} />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this collaborator?" onConfirm={() => del(confirm)} />
      <ConfirmDialog open={!!docConfirm} onOpenChange={(o) => !o && setDocConfirm(null)} title="Delete this document?" description="The file will be removed from this collaborator." onConfirm={() => removeDoc(docConfirm)} />
    </div>
  );
}
